import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import { cn } from "@/lib/utils";

/**
 * The tone tints the surface and colours the glyph; the words stay `foreground` and
 * `muted-foreground`. A notice whose every line is amber is a notice nobody reads twice — the
 * signal belongs in the glyph, which is also how `StatusPanel` carries it.
 *
 * The four tones are the status vocabulary `Badge` already publishes, at the same alpha steps,
 * so one word means one thing across the chip, the banner and the panel.
 */
const alertVariants = cva(
  "group/alert relative grid w-full gap-0.5 rounded-lg border px-2 py-1.5 text-left text-xs/relaxed has-data-[slot=alert-action]:relative has-data-[slot=alert-action]:pr-18 has-[>svg]:grid-cols-[auto_1fr] has-[>svg]:gap-x-1.5 *:[svg]:row-span-2 *:[svg]:translate-y-0.5 *:[svg:not([class*='size-'])]:size-3.5",
  {
    variants: {
      variant: {
        default: "border-border bg-card text-card-foreground *:[svg]:text-muted-foreground",
        info: "border-info/20 bg-info/10 text-foreground *:[svg]:text-info dark:bg-info/15",
        success:
          "border-success/20 bg-success/10 text-foreground *:[svg]:text-success dark:bg-success/15",
        warning:
          "border-warning/20 bg-warning/10 text-foreground *:[svg]:text-warning dark:bg-warning/15",
        destructive:
          "border-destructive/20 bg-destructive/10 text-foreground *:[svg]:text-destructive dark:bg-destructive/15",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
);

function Alert({
  className,
  variant,
  ...props
}: React.ComponentProps<"div"> & VariantProps<typeof alertVariants>) {
  return (
    <div
      data-slot="alert"
      role="alert"
      className={cn(alertVariants({ variant }), className)}
      {...props}
    />
  );
}

function AlertTitle({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="alert-title"
      className={cn(
        "font-medium group-has-[>svg]/alert:col-start-2 [&_a]:hover:text-foreground [&_a]:underline [&_a]:underline-offset-3",
        className
      )}
      {...props}
    />
  );
}

function AlertDescription({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="alert-description"
      className={cn(
        "text-muted-foreground text-xs/relaxed text-balance md:text-pretty [&_p:not(:last-child)]:mb-4 [&_a]:hover:text-foreground [&_a]:underline [&_a]:underline-offset-3",
        className
      )}
      {...props}
    />
  );
}

function AlertAction({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="alert-action"
      className={cn("absolute top-1.5 right-2", className)}
      {...props}
    />
  );
}

export { Alert, AlertTitle, AlertDescription, AlertAction };
